import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import type { ImportPreviewDto } from '@tiffinify/contracts';
import type { Slot } from '@tiffinify/core';
import { BtnPrimary, BtnSecondary, Card, ErrorText, Field, Helper, IconBtn, Badge, Mono, Steps } from '../components/ui';
import { Back, Card as PCard, Ic, Note, Row, Title, Top } from '../components/p';
import { api, post } from '../lib/api';
import { errorMessage, formatInr, localPhone, mealsLabel } from '../lib/format';

type Mode = 'photos' | 'csv' | 'paste';

interface Draft {
  key: number;
  name: string; phone: string; area: string; food: 'veg' | 'nonveg' | 'egg'; slot: Slot;
  planName: string; mealDays: number; startDate: string; price: string; paid: string; issues: string[];
}

const SAMPLE = 'Name,WhatsApp,Area,Diet,Meals,Start date,Days,Amount,Paid\nAsha Rao,98450 77777,Indiranagar,Veg,Lunch,2026-10-12,26,4000,2000\n';

export function Import() {
  const [params] = useSearchParams();
  const setup = params.get('setup') === '1' || params.get('from') === 'setup';
  const nav = useNavigate();
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>('csv');
  const [csv, setCsv] = useState('');
  const [rows, setRows] = useState<Draft[]>([]);
  const [reading, setReading] = useState(false);
  const [fileError, setFileError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = (p: ImportPreviewDto) => setRows(p.rows.map((r, i) => ({
    key: i, name: r.name, phone: r.phone ? localPhone(r.phone) : '', area: r.area, food: r.food, slot: r.slot, planName: r.planName, mealDays: r.mealDays,
    startDate: r.startDate ?? '', price: r.price ? String(r.price) : '', paid: r.paid ? String(r.paid) : '', issues: r.issues,
  })));

  const readCsv = useMutation({ mutationFn: (text: string) => post<ImportPreviewDto>('/import/csv', { csv: text }), onSuccess: load });
  const readPhoto = useMutation({
    mutationFn: (f: File) => api<ImportPreviewDto>('POST', '/import/photo', undefined, { raw: f, contentType: f.type }),
    onSuccess: load,
  });
  const commit = useMutation({
    mutationFn: () => post<{ created: number }>('/import/commit', {
      rows: rows.map((r) => ({ name: r.name, phone: r.phone, area: r.area, food: r.food, slot: r.slot, planName: r.planName, mealDays: r.mealDays, startDate: r.startDate, price: Number(r.price) || 0, paid: Number(r.paid) || 0 })),
    }),
    onSuccess: (r) => { void qc.invalidateQueries(); nav('/subscribers', { state: { flash: { text: r.created === 0 ? 'Nothing new to add: these customers are already there.' : `${r.created} ${r.created === 1 ? 'customer' : 'customers'} added.` } } }); },
  });

  const edit = (key: number, patch: Partial<Draft>) => setRows((cur) => cur.map((r) => (r.key === key ? { ...r, ...patch, issues: [] } : r)));
  const ready = rows.length > 0 && rows.every((r) => r.name.trim() && r.phone.replace(/\D/g, '').length >= 10 && /^\d{4}-\d{2}-\d{2}$/.test(r.startDate));
  const toCheck = rows.filter((r) => r.issues.length > 0).length;

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    setFileError('');
    if (mode === 'photos') { readPhoto.mutate(f); return; }
    if (f.size > 5_000_000) { setFileError('Choose a sheet smaller than 5 MB.'); return; }
    setReading(true);
    try {
      let text: string;
      if (/\.xlsx$/i.test(f.name)) {
        const { default: readSheet } = await import('read-excel-file');
        const cells = await readSheet(f);
        text = cells.map((row) => row.map((cell) => {
          const value = cell instanceof Date ? cell.toISOString().slice(0, 10) : String(cell ?? '');
          return '"' + value.replace(/"/g, '""') + '"';
        }).join(',')).join('\n');
      } else {
        text = await f.text();
      }
      setCsv(text);
      await readCsv.mutateAsync(text);
    } catch (error) {
      setFileError(errorMessage(error));
    } finally { setReading(false); }
  };
  const sample = () => {
    const url = URL.createObjectURL(new Blob([SAMPLE], { type: 'text/csv' }));
    const a = document.createElement('a'); a.href = url; a.download = 'tiffinify-customers.csv'; a.click(); URL.revokeObjectURL(url);
  };

  const pick = (m: Mode) => { setMode(m); setTimeout(() => fileRef.current?.click(), 0); };

  return (
    <main className="main task import">
      {setup ? (
        <div className="auth-block"><Mono>Step 3 of 3 · Your customers</Mono><Steps n={3} /></div>
      ) : <Top><Back to="/more" label="More" /></Top>}
      <Title h={setup ? 'Add your customers' : 'Import customers'} sub="From Excel, a list you already keep, or photos of your paper cards. You check every row before it is saved." />

      {rows.length === 0 ? (
        <>
          <PCard>
            <Row onClick={() => pick('csv')} left={<Ic icon="sheet" tone="s" />} t={reading ? "Reading the sheet…" : "Choose a file"} disabled={reading} s="Excel (.xlsx) or CSV" chev />
            <Row onClick={() => setMode('paste')} left={<Ic icon="edit" />} t="Paste rows" s="Name, phone, plan, start date" chev />
            <Row onClick={() => pick('photos')} left={<Ic icon="camera" />} t={readPhoto.isPending ? 'Reading the photo…' : 'Take or choose a photo'} s="Paper cards, several on one page" chev disabled={readPhoto.isPending} />
            <Row to="/new" left={<Ic icon="plus" />} t="One by one" s="A plan, a first payment and a welcome" chev />
            <Row onClick={sample} left={<Ic icon="down" />} t="Download a sample sheet" s="The columns we read" />
          </PCard>
          <input ref={fileRef} type="file" accept={mode === 'photos' ? 'image/jpeg,image/png,image/webp' : '.xlsx,.csv,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'} capture={mode === 'photos' ? 'environment' : undefined} hidden onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }} />
          <ErrorText>{fileError || (readPhoto.error ? errorMessage(readPhoto.error) : readCsv.error && mode !== 'paste' ? errorMessage(readCsv.error) : '')}</ErrorText>

          {mode === 'paste' && (
            <Card pad>
              <textarea className="plain" aria-label="CSV rows" placeholder={SAMPLE} value={csv} onChange={(e) => setCsv(e.target.value)} />
              <ErrorText>{readCsv.error ? errorMessage(readCsv.error) : ''}</ErrorText>
              <BtnPrimary busy={readCsv.isPending} label={readCsv.isPending ? 'Reading…' : 'Read these rows'} icon="arrow" onClick={() => readCsv.mutate(csv)} disabled={!csv.trim() || readCsv.isPending} />
            </Card>
          )}
          <Note>Anything we’re unsure about is marked, so you can fix it before saving. Each row becomes a customer with their plan and first payment.</Note>
          {setup && <div className="center-row"><Link className="text-link" to="/">Skip for now and open Today</Link></div>}
        </>
      ) : (
        <>
          <Card>
            <div className="between import-head">
              <b className="import-count">Ready to add · {rows.length} {rows.length === 1 ? 'customer' : 'customers'} read</b>
              {toCheck > 0 ? <Badge kind="due">{toCheck} to check</Badge> : <Badge kind="ok" icon="check">All set</Badge>}
            </div>
            {rows.map((r) => (
              <div key={r.key} className={`import-row${r.issues.length ? ' check' : ''}`}>
                <div className="between import-row-top">
                  <div className="grow import-row-text">
                    <span className="meta">{mealsLabel(r.slot)} · {r.mealDays} days · {r.food === 'nonveg' ? 'Non-veg' : r.food === 'egg' ? 'Egg' : 'Veg'}{r.area ? ` · ${r.area}` : ''}</span>
                    {r.issues.length > 0 && <span className="issues">{r.issues.join(' · ')}</span>}
                  </div>
                  <IconBtn icon="close" label={`Remove ${r.name || 'row'}`} ghost onClick={() => setRows((cur) => cur.filter((x) => x.key !== r.key))} />
                </div>
                <div className="two">
                  <Field label="Name" id={`i-n-${r.key}`} value={r.name} onChange={(v) => edit(r.key, { name: v })} />
                  <Field label="WhatsApp" id={`i-p-${r.key}`} value={r.phone} onChange={(v) => edit(r.key, { phone: v })} inputMode="tel" />
                </div>
                <div className="mini">
                  <Field label="Start" id={`i-s-${r.key}`} type="date" value={r.startDate} onChange={(v) => edit(r.key, { startDate: v })} />
                  <Field label="Price" id={`i-pr-${r.key}`} value={r.price} onChange={(v) => edit(r.key, { price: v.replace(/\D/g, '') })} inputMode="numeric" />
                  <Field label="Paid" id={`i-pd-${r.key}`} value={r.paid} onChange={(v) => edit(r.key, { paid: v.replace(/\D/g, '') })} inputMode="numeric" />
                </div>
                {r.price && <span className="meta">Balance {formatInr(Math.max(0, Number(r.price) - (Number(r.paid) || 0)))}</span>}
              </div>
            ))}
          </Card>
          <Helper icon="info">End dates are worked out from your delivery days and holidays when you save. Customers with a number you already have get a new plan, not a duplicate.</Helper>
          <ErrorText>{commit.error ? errorMessage(commit.error) : ''}</ErrorText>
          <div className="auth-block">
            <BtnPrimary busy={commit.isPending} label={commit.isPending ? 'Saving…' : `Add ${rows.length} ${rows.length === 1 ? 'customer' : 'customers'}`} icon="check" onClick={() => commit.mutate()} disabled={!ready || commit.isPending} />
            <BtnSecondary label="Start over" onClick={() => setRows([])} />
          </div>
        </>
      )}
    </main>
  );
}
